import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Trophy, Clock, MapPin } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLanguage } from '@/contexts/LanguageContext';
import type { EliminationSeed } from '@/hooks/useEliminationSeeding';

interface EliminationSeedingTableProps {
  seeding: EliminationSeed[];
  loading: boolean;
  subcategory: string;
  category: string;
  hidePointsAndSets?: boolean;
}

const EliminationSeedingTable = ({ 
  seeding, 
  loading, 
  subcategory, 
  category,
  hidePointsAndSets = false
}: EliminationSeedingTableProps) => {
  const isMobile = useIsMobile();
  const { t } = useLanguage();

  const getQualificationLabel = (type: string) => {
    switch (type) {
      case 'first_place': return t('tournament.firstPlace');
      case 'second_place': return t('tournament.secondPlace');
      case 'wildcard': return t('tournament.wildcard');
      default: return type;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            <span>{t('tournament.classifiedForElimination')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (seeding.length === 0) {
    return (
      <Card className="border-amber-200 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            <span>{t('tournament.classifiedForElimination')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-6 space-y-2">
            <p className="text-sm text-amber-700 dark:text-amber-300 font-medium">
              {t('tournament.waitingGroupPhase')}
            </p>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              {t('tournament.waitingGroupPhaseDesc')}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isMobile) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" />
              <span>{t('tournament.classifiedForElimination')}</span>
            </CardTitle>
            <Badge variant="secondary" className="text-xs">
              {category} {subcategory}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3 pt-0">
          {seeding.map((seed) => (
            <div
              key={seed.seed}
              className={`p-4 rounded-lg border-l-4 ${
                seed.isUser 
                  ? 'border-l-primary bg-primary/5 dark:bg-primary/10' 
                  : 'border-l-gray-300 bg-white dark:bg-gray-800'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-muted-foreground w-8">
                    {seed.seed}
                  </span>
                  <div>
                    <div className="font-semibold text-sm">
                      {seed.player1Name}
                      {seed.player2Name && ` / ${seed.player2Name}`}
                    </div>
                    <div className="flex items-center gap-1 text-xs text-muted-foreground mt-0.5">
                      <MapPin className="h-3 w-3" />
                      <span>
                        {seed.fromGroup} · {getQualificationLabel(seed.qualificationType)}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1">
                  {seed.isUser && (
                    <Badge className="bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary text-xs">
                      {t('tournament.you')}
                    </Badge>
                  )}
                  {seed.qualificationType === 'wildcard' && (
                    <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                      WC
                    </Badge>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2 pt-2 border-t">
                {!hidePointsAndSets && (
                  <span className="font-medium">
                    {seed.points} {t('tournament.pts').toLowerCase()}
                  </span>
                )}
                {!hidePointsAndSets && (
                  <span>
                    {t('tournament.sets')}: {seed.setsWon}-{seed.setsLost}
                    <span className={`ml-1 ${seed.setsWon - seed.setsLost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ({seed.setsWon - seed.setsLost >= 0 ? '+' : ''}{seed.setsWon - seed.setsLost})
                    </span>
                  </span>
                )}
                <span>
                  {t('tournament.games')}: {seed.gamesWon}-{seed.gamesLost}
                  {hidePointsAndSets && (
                    <span className={`ml-1 ${seed.gamesWon - seed.gamesLost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      ({seed.gamesWon - seed.gamesLost >= 0 ? '+' : ''}{seed.gamesWon - seed.gamesLost})
                    </span>
                  )}
                </span>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Trophy className="h-5 w-5 text-amber-500" />
            <span>{t('tournament.classifiedForElimination')}</span>
          </CardTitle>
          <Badge variant="secondary">
            {category} {subcategory}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left">
                <th className="py-2 px-2 font-medium text-muted-foreground w-12">#</th>
                <th className="py-2 px-2 font-medium text-muted-foreground">{t('tournament.team')}</th>
                <th className="py-2 px-2 font-medium text-muted-foreground">{t('tournament.groups')}</th>
                {!hidePointsAndSets && <th className="py-2 px-2 font-medium text-muted-foreground text-center">{t('tournament.pts')}</th>}
                {!hidePointsAndSets && <th className="py-2 px-2 font-medium text-muted-foreground text-center">{t('tournament.sets')}</th>}
                <th className="py-2 px-2 font-medium text-muted-foreground text-center">{t('tournament.games')}</th>
              </tr>
            </thead>
            <tbody>
              {seeding.map((seed) => (
                <tr 
                  key={seed.seed}
                  className={`border-b ${seed.isUser ? 'bg-primary/5 dark:bg-primary/10' : ''}`}
                >
                  <td className="py-3 px-2">
                    <span className="text-lg font-bold text-muted-foreground">{seed.seed}</span>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">
                        {seed.player1Name}
                        {seed.player2Name && ` / ${seed.player2Name}`}
                      </span>
                      {seed.isUser && (
                        <Badge className="bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary text-xs">
                          {t('tournament.you')}
                        </Badge>
                      )}
                      {seed.qualificationType === 'wildcard' && (
                        <Badge variant="outline" className="text-xs border-amber-400 text-amber-600">
                          WC
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <span className="text-muted-foreground">{seed.fromGroup}</span>
                    <span className="text-xs ml-1 text-muted-foreground">
                      ({getQualificationLabel(seed.qualificationType)})
                    </span>
                  </td>
                  {!hidePointsAndSets && (
                    <td className="py-3 px-2 text-center font-medium">{seed.points}</td>
                  )}
                  {!hidePointsAndSets && (
                    <td className="py-3 px-2 text-center">
                      {seed.setsWon}-{seed.setsLost}
                      <span className={`ml-1 text-xs ${seed.setsWon - seed.setsLost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ({seed.setsWon - seed.setsLost >= 0 ? '+' : ''}{seed.setsWon - seed.setsLost})
                      </span>
                    </td>
                  )}
                  <td className="py-3 px-2 text-center">
                    {seed.gamesWon}-{seed.gamesLost}
                    {hidePointsAndSets && (
                      <span className={`ml-1 text-xs ${seed.gamesWon - seed.gamesLost >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        ({seed.gamesWon - seed.gamesLost >= 0 ? '+' : ''}{seed.gamesWon - seed.gamesLost})
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default EliminationSeedingTable;